import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import SimpleDashboard from './components/SimpleDashboard';
import ConversationsPage from './components/ConversationsPage';
import SimpleAnalyticsPage from './components/SimpleAnalyticsPage';
import RatingsPage from './components/RatingsPage';
import AIDataPage from './components/AIDataPage';
import SimpleSettingsPage from './components/SimpleSettingsPage';
import UISettingsPage from './components/UISettingsPage';
import CTAButtonsPage from './components/CTAButtonsPage';
import TestEmailPage from './components/TestEmailPage';
import DailyReportSettings from './components/DailyReportSettings';
import StatusPage from './components/StatusPage';
import AvailabilityPage from './components/AvailabilityPage';
import VoiceAIPage from './components/VoiceAIPage';
import Layout from './components/Layout';
import { configService, BusinessConfig } from './services/configService';

function AppContent() {
  const [config, setConfig] = useState<BusinessConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const { user, logout } = useAuth();

  useEffect(() => {
    // Load config
    const loadConfig = async () => {
      try {
        const businessConfig = await configService.getConfig();
        setConfig(businessConfig);
      } catch (err) {
        console.error('Failed to load config:', err);
        // Set a default config if loading fails
        setConfig({
          businessName: 'Adrift Tongariro',
          welcomeMessage: 'Welcome to Adrift Admin Dashboard',
          chatPlaceholder: 'Type your message...',
          theme: {
            primaryColor: '#ff7f00',
            secondaryColor: '#1565c0',
            backgroundColor: '#f5f5f5',
            textColor: '#333333'
          },
          ai: {
            model: 'gpt-3.5-turbo',
            temperature: 0.7,
            maxTokens: 800,
            systemPrompt: 'You are a helpful AI assistant.'
          },
          rateLimiting: {
            enabled: true,
            maxRequestsPerHour: 100,
            maxRequestsPerDay: 500
          },
          notifications: {
            email: 'admin@example.com',
            newChatAlert: true,
            dailyReport: true
          },
          rag: {
            enabled: true,
            maxResults: 5,
            similarityThreshold: 0.1
          }
        });
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  if (configLoading || !config) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          user ? (
            <Navigate to="/" replace />
          ) : (
            <Container maxWidth="sm">
              <Box mt={8}>
                <LoginPage config={config} />
              </Box>
            </Container>
          )
        } 
      />
      <Route 
        path="/*" 
        element={
          <ProtectedRoute>
            {user && (
              <Layout user={user} onLogout={logout} config={config}>
                <Routes>
                  <Route path="/" element={<SimpleDashboard />} />
                  <Route path="/conversations" element={<ConversationsPage />} />
                  <Route path="/analytics" element={<SimpleAnalyticsPage />} />
                  <Route path="/ratings" element={<RatingsPage />} />
                  <Route path="/ai-data" element={<AIDataPage />} />
                  <Route path="/cta-buttons" element={<CTAButtonsPage />} />
                  <Route path="/settings" element={<SimpleSettingsPage />} />
                  <Route path="/ui-settings" element={<UISettingsPage />} />
                  <Route path="/test-email" element={<TestEmailPage />} />
                  <Route path="/daily-reports" element={<DailyReportSettings />} />
                  <Route path="/status" element={<StatusPage />} />
                  <Route path="/availability" element={<AvailabilityPage />} />
                  <Route path="/voice-ai" element={<VoiceAIPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 